import { AfterViewInit, Component, OnInit } from '@angular/core';
import { BUTTONS, ChassisFormHandler, LoaderService, PluginDataInterface, RouteService, ToastService2, ToolbarService, trans } from '@kaiser-software/chassis-core';
import { ObservationModel } from '../../../models/observation.model';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { ObservationService } from '../../../services/observation.service';
import { Observable, Observer, Subject } from 'rxjs';
import { CreateObservationFormFactory } from '../../../services/create-observation-form.factory';
import { first, map } from 'rxjs/operators';

const SAVE_AND_COMPLETE = 'save-and-complete';

@Component({
    selector: 'app-observations-create',
    templateUrl: 'create.component.html',
    styleUrls: ['create.component.scss'],
    providers: [
        CreateObservationFormFactory
    ],
})
export class ObservationsCreateComponent implements OnInit, AfterViewInit {

    public form: ChassisFormHandler;

    /**
     * The observation model subject.
     *
     * @type {ReplaySubject<any>}
     */
    protected $observation: Subject<ObservationModel> = new ReplaySubject(1);

    /**
     * The data passed to the plugin slots.
     *
     * @type {PluginDataInterface}
     */
    protected pluginData: PluginDataInterface;

    protected loader;

    constructor(
        protected observationService: ObservationService,
        protected createObservationFormFactory: CreateObservationFormFactory,
        protected toolbarService: ToolbarService,
        protected routeService: RouteService,
        protected toastService: ToastService2,
        protected loaderService: LoaderService) {
    }

    public ngOnInit() {
        const model = this.observationService.make();

        this.$observation.next(model);

        this.loader = this.loaderService.create();

        this.setUpForm();
        this.setPluginData();
    }

    public ngAfterViewInit() {
        this.setUpToolbar();
    }

    protected setUpForm(): void {
        this.form = this.createObservationFormFactory.create(
            this.$observation
        );
    }

    protected setPluginData(): void {
        this.pluginData = {
            inputs: {
                form: this.form,
                model: this.$observation,
                modelSubject: this.$observation,
            }
        };
    }

    protected setUpToolbar() {
        this.removeDefaultSaveButton();

        this.addCreateWithOpenToolbarButton();
        this.addCreateAndCompleteToolbarButton();
    }

    protected removeDefaultSaveButton() {
        this.toolbarService.remove(BUTTONS.SAVE.NAME);
        this.toolbarService.remove(BUTTONS.SAVE_BACK_TO_INDEX.NAME);
    }

    protected addCreateAndCompleteToolbarButton(): void {
        this.$observation
            .pipe(
                first(),
            )
            .subscribe((observation: ObservationModel) => {
                this.canCreateAndComplete()
                    .subscribe((value) => {
                        if (value) {
                            if (this.toolbarService.get(SAVE_AND_COMPLETE)) {
                                return;
                            }

                            this.toolbarService.add({
                                name: SAVE_AND_COMPLETE,
                                text: 'Save and complete',
                                classes: 'btn-success',
                                icon: 'fal fa-check',
                                callback: () => {
                                    this.sendObservationCreateAndCompleteRequest(observation);
                                }
                            });
                        } else {
                            this.toolbarService.remove(SAVE_AND_COMPLETE);
                        }
                    });
            });
    }

    protected canCreateAndComplete(): Observable<boolean> {
        return this.form.formGroup.valueChanges
            .pipe(
                map(() => {
                    return this.form.value
                        && this.form.value.category
                        && !this.form.value.category.is_urgent;
                }),
            );
    }

    protected addCreateWithOpenToolbarButton(): void {
        this.$observation
            .pipe(
                first(),
            )
            .subscribe((observation: ObservationModel) => {
                this.canSaveForm()
                    .subscribe((value) => {
                        if (value) {
                            if (this.toolbarService.get('save-with-open')) {
                                return;
                            }

                            this.toolbarService.add({
                                name: 'save-with-open',
                                text: trans('Save as concept'),
                                classes: 'btn-default',
                                icon: 'fal fa-spinner',
                                callback: () => {
                                    this.sendObservationCreateWithOpenRequest(observation);
                                },
                                disabled: () => {
                                    return this.loader.isLoading();
                                },
                                loader: () => {
                                    return this.loader.isLoading();
                                }
                            });
                        } else {
                            this.toolbarService.remove('save-with-open');
                        }
                    });
            });
    }

    protected canSaveForm(): Observable<boolean> {
        return this.form.formGroup.valueChanges
            .pipe(
                map(() => {
                    return this.form.value
                        && this.form.value.category;
                }),
            );
    }

    protected sendObservationCreateAndCompleteRequest(observation: ObservationModel): Observable<ObservationModel> {
        this.loader.start();

        observation.fill({
            'status': 'completed',
        });

        const request = this.createObservation(observation);

        request
            .subscribe(() => {
                this.toastService.success(
                    trans('Successfully completed the observation')
                );

                this.loader.stop();
            }, () => {
                this.loader.stop();
            });

        return request;
    }

    protected sendObservationCreateWithOpenRequest(observation: ObservationModel) {
        observation.fill({
            'status': 'open',
        });

        const request = this.createObservation(observation);

        request
            .subscribe(() => {
                this.toastService.success(
                    trans('Successfully saved the observation')
                );
            });

        return request;
    }

    protected createObservation(observation: ObservationModel): Observable<ObservationModel> {
        return new Observable((observer: Observer<ObservationModel>) => {
            this.sendCreateObservationRequest(observation)
                .subscribe((o: ObservationModel) => {
                    observer.next(o);

                    this.navigateToEditPage(o);
                }, (error) => {
                    observation.fill({
                        status: 'concept'
                    });

                    this.toastService.error(error.message);

                    observer.error(error);
                }, () => {
                    observer.complete();
                });
        });
    }

    protected sendCreateObservationRequest(observation: ObservationModel): Observable<ObservationModel> {
        return this.observationService.post(
            observation.plain()
        );
    }

    protected navigateToEditPage(observation: ObservationModel) {
        this.routeService.navigate('observations.edit', observation.id);
    }

}



// WEBPACK FOOTER //
// ./apps/eyewatch/src/app/eyewatch/observations/pages/observations/create/create.component.ts